import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useMemo} from 'react';

import {isEmpty, isNil, isNotEmpty} from 'ramda';

import {
  useAuctionBulkDeleteUserComparisonMutation,
  useGetAuctionUserComparisonQuery,
  useAuctionReorderUserComparisonMutation,
  useSourcingBulkDeleteUserComparisonMutation,
  useSourcingListUserComparisonQuery,
  useSourcingReorderUserComparisonMutation,
} from '@dms/api/core';
import {useGetUserStatsQuery} from '@dms/api/customer';
import {i18n} from '@dms/i18n';
import {COUNTRY_CZE, CURRENCY_CZK, formatDocumentTitle, handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {adaptAuctionVehicleToSourcingItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {ComparisonPageHeader} from './components/ComparisonPageHeader';
import {LabelColumn} from './components/LabelColumn';
import {SortableVehicleColumn} from './components/SortableVehicleColumn';
import {getMinMaxValues} from './utils/getMinMaxValues';
import {sortAndUpdateVehicleFeatures} from './utils/sortAndUpdateVehicleFeatures';

export function UserComparisonPage(props: TestIdProps) {
  const {data: userStats, isLoading: isUserStatsLoading} = useGetUserStatsQuery();
  const comparisonCount = userStats?.userStats.comparisonCount;
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();
  const [reorderClassifiedsVehicles] = useSourcingReorderUserComparisonMutation();
  const [reorderAuctionVehicles] = useAuctionReorderUserComparisonMutation();
  const [deleteClassifiedsComparison, {isLoading: isDeletingClassifieds}] =
    useSourcingBulkDeleteUserComparisonMutation();
  const [deleteAuctionComparison, {isLoading: isDeletingAuctions}] =
    useAuctionBulkDeleteUserComparisonMutation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const {data: classifiedsData, isLoading: isClassifiedsLoading} =
    useSourcingListUserComparisonQuery(
      {
        currency: currency?.code ?? CURRENCY_CZK,
        buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
        size: comparisonCount,
      },
      {skip: isNil(comparisonCount) || sourcingType === SourcingType.AUCTIONS}
    );

  const {data: auctionsData, isLoading: isAuctionsLoading} = useGetAuctionUserComparisonQuery(
    {
      currency: currency?.code ?? CURRENCY_CZK,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      size: comparisonCount,
    },
    {skip: isNil(comparisonCount) || sourcingType === SourcingType.CLASSIFIEDS}
  );

  const vehicles = useMemo(
    () =>
      sourcingType === SourcingType.CLASSIFIEDS
        ? (classifiedsData?.data ?? [])
        : (auctionsData?.data?.map((item) => adaptAuctionVehicleToSourcingItem(item, enumLookup)) ??
          []),
    [auctionsData, classifiedsData, enumLookup, sourcingType]
  );
  const isLoading =
    sourcingType === SourcingType.CLASSIFIEDS ? isClassifiedsLoading : isAuctionsLoading;
  const vehicleIds = useMemo(
    () => vehicles.map((vehicle) => vehicle.sourcingVehicle.id),
    [vehicles]
  );
  const auctionVehiclesOriginal = useMemo(() => auctionsData?.data ?? [], [auctionsData]);
  const vehiclesWithFeatures = useMemo(() => sortAndUpdateVehicleFeatures(vehicles), [vehicles]);
  const minMaxValues = useMemo(() => getMinMaxValues(vehicles), [vehicles]);

  const isDeleting =
    sourcingType === SourcingType.CLASSIFIEDS ? isDeletingClassifieds : isDeletingAuctions;

  const handleDelete = () => {
    const externalIds = vehicles.map((vehicle) => vehicle.sourcingVehicle.adId).filter(isNotEmpty);

    if (sourcingType === SourcingType.CLASSIFIEDS) {
      deleteClassifiedsComparison({body: {externalIds}}).unwrap().catch(handleApiError);
    }

    if (sourcingType === SourcingType.AUCTIONS) {
      deleteAuctionComparison({body: {externalIds}}).unwrap().catch(handleApiError);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = vehicles.findIndex((vehicle) => vehicle.sourcingVehicle.id === active.id);
    const newIndex = vehicles.findIndex((vehicle) => vehicle.sourcingVehicle.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      if (sourcingType === SourcingType.CLASSIFIEDS) {
        const reorderedVehicles = arrayMove(vehicles, oldIndex, newIndex);
        reorderClassifiedsVehicles(reorderedVehicles).unwrap().catch(handleApiError);
      }

      if (sourcingType === SourcingType.AUCTIONS) {
        const reorderedAuctionVehicles = arrayMove(auctionVehiclesOriginal, oldIndex, newIndex);
        reorderAuctionVehicles(reorderedAuctionVehicles).unwrap().catch(handleApiError);
      }
    }
  };

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('general.actions.comparison'),
          sourcingType === SourcingType.CLASSIFIEDS
            ? i18n.t('page.sourcing.labels.classifieds')
            : i18n.t('page.sourcing.labels.auctions'),
          i18n.t('page.sourcing.labels.sourcing')
        )}
      />
      <SourcingDataStatus
        isEmpty={!isLoading && isEmpty(vehicles)}
        iconUrl="/assets/images/empty_images/empty-compare.svg"
        text={i18n.t('entity.vehicle.notifications.nothingInComparison')}
      >
        <DataStatus isLoading={isLoading || isUserStatsLoading} minHeight="100%">
          <Box overflow="auto" width="100%" height="100%" position="relative">
            <ComparisonPageHeader
              isLoading={isLoading}
              isDeleting={isDeleting}
              onDelete={handleDelete}
              data-testid={suffixTestId('comparisonPageHeader', props)}
            />
            <HStack>
              <LabelColumn />
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={vehicleIds} strategy={horizontalListSortingStrategy}>
                  {vehiclesWithFeatures.map((vehicle) => (
                    <SortableVehicleColumn
                      key={vehicle.sourcingVehicle.id}
                      vehicle={vehicle}
                      minMaxValues={minMaxValues}
                      data-testid={suffixTestId('vehicleColumn', props)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </HStack>
          </Box>
        </DataStatus>
      </SourcingDataStatus>
    </>
  );
}
